import posthog from 'posthog-js';
import { useCallback, useMemo } from 'react';

import { useProjectAnalytics } from '@/contexts/project-analytics-context';

// PostHog is initialized in instrumentation-client.ts
// This file provides a clean abstraction layer for analytics
// Makes it easy to swap providers (PostHog → Amplitude → etc.)

// Export PostHog instance for advanced use cases (feature flags, etc.)
export { posthog };

// Helper function to check if user is authenticated
// Note: This checks if PostHog has an identified user
// For better-auth integration, pass session status explicitly to trackEvent
const getAuthStatus = (): boolean => {
  // For server-side usage
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Check if PostHog has an identified user
    // PostHog sets distinct_id when identify() is called
    const distinctId = posthog.get_distinct_id();
    const isIdentified = distinctId && !distinctId.startsWith('$');
    return Boolean(isIdentified);
  } catch (error) {
    console.error('Error checking auth status:', error);
    return false;
  }
};

// Helper to extract page name from route
function getPageFromRoute(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const path = window.location.pathname;

  // Match patterns like /project/[id]/dashboard, /project/[id]/qna, etc.
  const match = path.match(/\/project\/[^/]+\/([^/?]+)/);
  if (!match?.[1]) return undefined;

  // Convert route segment to readable page name
  const segment = match[1];
  const pageMap: Record<string, string> = {
    dashboard: 'project_dashboard',
    qna: 'requirement_questions',
    theme: 'theme_customization',
    wireframes: 'wireframes',
    frs: 'frs_review',
    pricing: 'pricing_approval',
    waiting: 'build_waiting',
    ready: 'build_ready',
    editor: 'code_editor',
    settings: 'product_settings',
    integrations: 'integrations',
    'version-history': 'version_history',
    'task-list': 'task_list',
    database: 'database',
    storage: 'storage',
    'custom-domain': 'custom_domain',
  };

  return pageMap[segment] || segment;
}

// Core tracking function (can be used outside React components)
export function trackEventCore(
  event: ANALYTICS_EVENTS,
  properties: any = {},
  options?: { isSignedIn?: boolean }
) {
  const finalProps = { ...properties };

  // Extract project ID from URL (existing logic)
  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(
      /\/(?:workspace|project)\/([a-zA-Z0-9]+)/
    );
    if (match?.[1]) {
      finalProps.project_id = finalProps.project_id || match[1]; // Use snake_case for consistency
    }

    // Auto-add page (page name from route) if not manually provided
    if (!finalProps.page) {
      const pageName = getPageFromRoute();
      if (pageName) {
        finalProps.page = pageName;
      }
    }
  }

  // Add authentication status (existing logic)
  finalProps.loginStatus =
    options?.isSignedIn !== undefined ? options.isSignedIn : getAuthStatus();

  // Track with PostHog
  posthog.capture(event, finalProps);
}

// Backward compatible alias
export const trackEvent = trackEventCore;

/**
 * React hook for automatic project properties from context
 * Use this in project pages instead of trackEvent for auto-included project metadata
 *
 * Usage:
 *   const track = useAnalyticsTracker();
 *   track(ANALYTICS_EVENTS.button_clicked, { section: 'home' });
 *
 * Auto-included properties:
 *   - project_id: From context or URL extraction
 *   - project_name: From context (project name)
 *   - project_url: From context (deployment URL)
 *   - project_editor_url: From context (editor service URL)
 *   - project_role: From context (user's role: project_owner | admin | viewer | workspace_owner | no_role)
 *   - page: Auto-detected from route
 *   - loginStatus: Auto-detected from auth state
 *
 * Note: PostHog automatically includes $current_url, $pathname, etc.
 */
export function useAnalyticsTracker() {
  const { projectData, isLoading, error } = useProjectAnalytics();

  // Memoize only the essential project data properties to prevent unnecessary re-renders
  const memoizedProjectData = useMemo(() => {
    if (!projectData) return null;
    return {
      project_id: projectData.project_id,
      project_name: projectData.project_name,
      project_url: projectData.project_url,
      project_editor_url: projectData.project_editor_url,
      project_role: projectData.project_role,
    };
  }, [
    projectData?.project_id,
    projectData?.project_name,
    projectData?.project_url,
    projectData?.project_editor_url,
    projectData?.project_role,
  ]);

  // Stabilize the tracking function reference using useCallback
  return useCallback(
    (
      event: ANALYTICS_EVENTS,
      properties: any = {},
      options?: { isSignedIn?: boolean }
    ) => {
      const finalProps = { ...properties };

      // Auto-merge project properties from context if available
      if (memoizedProjectData) {
        finalProps.project_id =
          finalProps.project_id || memoizedProjectData.project_id;
        finalProps.project_name =
          finalProps.project_name || memoizedProjectData.project_name;
        finalProps.project_url =
          finalProps.project_url || memoizedProjectData.project_url;
        finalProps.project_editor_url =
          finalProps.project_editor_url ||
          memoizedProjectData.project_editor_url;
        finalProps.project_role =
          finalProps.project_role || memoizedProjectData.project_role;
      } else if (typeof window !== 'undefined') {
        const isProjectRoute = /\/project\/[^/]+/.test(
          window.location.pathname
        );

        if (isProjectRoute && !isLoading && !error) {
          // Project route, not loading, no error, but no data = unexpected
          console.warn(
            `[Analytics] Project data unexpectedly missing for event: ${event}. ` +
              `Event will fire with partial data.`
          );
        } else if (isProjectRoute && isLoading) {
          // Project route, still loading = expected during mount
          console.debug(
            `[Analytics] Event "${event}" fired while project data loading. ` +
              `This is normal on slow connections.`
          );
        } else if (isProjectRoute && error) {
          // Project route with error = log for monitoring
          console.error(
            `[Analytics] Event "${event}" fired but project data failed to load:`,
            error.message
          );
        }
        // Non-project routes: no warning needed
      }

      // Call core tracking function (which adds page_url and page automatically)
      trackEventCore(event, finalProps, options);
    },
    [memoizedProjectData, isLoading, error]
  );
}

/**
 * Identify user with analytics provider
 * PostHog handles deduplication internally - safe to call multiple times
 */
export function identifyUser(
  userId: string,
  traits: {
    email: string;
    avatar: string;
    name: string;
    emailVerified: boolean;
  }
) {
  // Identify with PostHog
  posthog.identify(userId, traits);

  // Set person properties for filtering in PostHog
  posthog.setPersonProperties({
    email: traits.email,
    name: traits.name,
    avatar: traits.avatar,
    emailVerified: traits.emailVerified,
  });
}

/**
 * Reset user identity (call on logout)
 * Clears PostHog's stored user data and generates new anonymous ID
 */
export function resetUser() {
  posthog.reset();
}

// Helper hook for React components using better-auth
// Usage: const track = useAnalytics();
//        track(ANALYTICS_EVENTS.BUTTON_CLICK, { button: 'signup' });
export function createTracker(isSignedIn?: boolean) {
  return (event: ANALYTICS_EVENTS, properties?: any) => {
    trackEvent(event, properties, { isSignedIn });
  };
}

export enum ANALYTICS_EVENTS {
  // ============================================
  // LANDING & WEBSITE (1 event)
  // ============================================

  /** User lands on Creatr website homepage */
  //Posthog automatically tracks this event
  creatr_website_landing = 'creatr_website_landing',

  // ============================================
  // AUTHENTICATION FLOW (6 events)
  // ============================================

  /**
   * User lands on the signup page
   * Properties: utm_source
   */
  //Posthog automatically tracks this event
  signup_page_landing = 'signup_page_landing',

  /**
   * User enters email and clicks continue
   * Properties:
   * - auth_type: "signup" | "login"
   * - user_type: "new" | "existing"
   * - user_email: string
   * - is_first_login: boolean
   */
  signup_email_entered = 'signup_email_entered',

  /**
   * User enters invite code and clicks Continue
   * Properties: inviteCode: string
   */
  signup_invite_submitted = 'signup_invite_submitted',

  /**
   * User enters OTP received over email
   * Properties: user_type: "new" | "existing"
   */
  signup_otp_entered = 'signup_otp_entered',

  /**
   * User login/signup is successful
   * Properties:
   * - auth_type: "signup" | "login"
   * - user_type: "new" | "existing"
   * - user_email: string
   * - is_first_login: boolean
   */
  user_login_success = 'user_login_success',

  // ============================================
  // CREATE PAGE (3 events)
  // ============================================

  /**
   * User has landed on home page with prompt
   * Properties:
   * - config_type: "attach" | "select_theme" | "record_audio"
   * - action: "opened" | "completed"
   * - source: "prompt_box"
   * - page: "create_page"
   */
  create_page_landing = 'create_page_landing',

  /**
   * User clicks on a config option (attach/theme/audio)
   * Properties:
   * - config_type: "attach" | "select_theme" | "record_audio"
   * - action: "opened" | "completed"
   * - source: "prompt_box"
   * - page: "create_page"
   */
  config_clicked = 'config_clicked',

  /**
   * User submits prompt text to create project
   * Properties:
   * - prompt: string (text value entered by user)
   * - configs: { attach_enabled, record_audio_enabled, theme_enabled }
   * - theme: { type, name, meta: { primary_color, secondary_color, font_family, font_weight, border_radius } }
   * - attachments: { is_attached, count, types: string[] }
   * - submit_source: "prompt_box"
   * - page: "create_page"
   */
  prompt_text_submitted = 'prompt_text_submitted',

  // ============================================
  // NOTIFICATIONS (2 events)
  // ============================================

  /**
   * User clicks notification icon in top nav
   * Properties:
   * - nav_item: "notifications"
   * - action: "opened" | "closed"
   * - page: "create_page" | "project_dashboard"
   * - has_unread: boolean
   */
  notification_icon_clicked = 'notification_icon_clicked',

  /**
   * User switches tabs in notifications panel
   * Properties:
   * - tab_from: string
   * - tab_to: "whats_new"
   * - has_unread: boolean
   * - entry_point: "top_nav"
   * - page: "create_page" | "project_dashboard"
   */
  notification_tab_switched = 'notification_tab_switched',

  // ============================================
  // SETTINGS (7 events)
  // ============================================

  /**
   * User clicks settings icon in top nav
   * Properties:
   * - nav_item: "settings"
   * - action: "opened" | "closed"
   * - page: "create_page"
   */
  settings_icon_clicked = 'settings_icon_clicked',

  /**
   * User views a settings section
   * Properties:
   * - section: "profile" | "notifications" | "logout"
   * - surface: "settings_dialog"
   * - entry_point: "top_nav"
   */
  settings_section_viewed = 'settings_section_viewed',

  /**
   * User updates profile information
   * Properties:
   * - fields_updated: string[] (e.g., ["full_name", "avatar"])
   * - page: "create_page"
   */
  settings_profile_updated = 'settings_profile_updated',

  /**
   * User updates contact information
   * Properties:
   * - country_code: string
   * - number: string
   * - channel_enabled_for: string[] (e.g., ["sms", "whatsapp"])
   */
  settings_contact_updated = 'settings_contact_updated',

  /**
   * User updates notification preferences
   * Properties:
   * - channel: "whatsapp" | "sms" | "browser_push"
   * - enabled: boolean
   */
  settings_notification_updated = 'settings_notification_updated',

  /** User initiates logout */
  settings_logout_initiated = 'settings_logout_initiated',

  /**
   * User confirms or cancels logout
   * Properties: action: "confirmed" | "cancelled"
   */
  settings_logout_actiontaken = 'settings_logout_actiontaken',

  // ============================================
  // PROJECTS LIST (2 events)
  // ============================================

  /**
   * User switches between project tabs
   * Properties:
   * - tab_from: string
   * - tab_to: "shared_projects"
   * - project_count_visible: number
   */
  projects_tab_switched = 'projects_tab_switched',

  /**
   * User opens a project
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_status: "ready" | "wireframing" | "collecting_requirements"
   * - is_shared: boolean
   * - origin_tab: "your_projects"
   */
  project_opened = 'project_opened',

  // ============================================
  // PROJECT DASHBOARD (18 events)
  // ============================================

  /**
   * User lands on project dashboard
   * Properties:
   * - section: string
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - project_status: "ready"
   * - entry_point: "projects_page"
   * - origin_tab: "your_projects"
   * - page: "home" | "product_settings" | "version_history" | "task_list" | "integrations"
   */
  project_dashboard_landing = 'project_dashboard_landing',

  /**
   * User clicks on project side menu item
   * Properties:
   * - section: "home" | "product_settings" | "version_history"
   * - project_id: string
   * - project_name: string
   * - project_url: string
   */
  project_sidemenu_clicked = 'project_sidemenu_clicked',

  /**
   * User clicks edit button for project
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - section: "home"
   */
  project_edit_clicked = 'project_edit_clicked',

  /**
   * User clicks on integrations
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - origin_section: "home"
   * - source: "Title" | "Button"
   */
  project_integrations_clicked = 'project_integrations_clicked',

  /**
   * User clicks to rerun tests
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - test_type: "speed_insights"
   * - section: "home"
   */
  project_testRerun_clicked = 'project_testRerun_clicked',

  /**
   * User clicks invite button
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - page: "project_dashboard" | "requirement_questions"
   */
  project_invite_clicked = 'project_invite_clicked',

  /**
   * User sends project invite (submits invite form)
   * Properties:
   * - project_id: string
   * - email: string
   * - role: "project_owner" | "admin" | "viewer"
   */
  project_invite_member = 'project_invite_member',

  /**
   * User sends project invite
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - role_assigned: "viewer"
   * - invite_method: "email"
   */
  project_invite_sent = 'project_invite_sent',

  /**
   * User opens project share popover
   * Properties:
   * - project_id: string
   * - context: "wireframes" | "dashboard" | "other"
   */
  project_share_popover_opened = 'project_share_popover_opened',

  /**
   * User closes project share popover
   * Properties:
   * - project_id: string
   */
  project_share_popover_closed = 'project_share_popover_closed',

  /**
   * User changes role dropdown in invite form input
   * Properties:
   * - project_id: string
   * - from_role: "project_owner" | "admin" | "viewer"
   * - to_role: "project_owner" | "admin" | "viewer"
   */
  project_invite_input_role_changed = 'project_invite_input_role_changed',

  /**
   * Invite attempt failed
   * Properties:
   * - project_id: string
   * - role: "project_owner" | "admin" | "viewer"
   * - error_message: string
   */
  project_invite_error = 'project_invite_error',

  /**
   * Member role updated
   * Properties:
   * - project_id: string
   * - member_id: string
   * - from_role: string
   * - to_role: string
   */
  project_member_role_updated = 'project_member_role_updated',

  /**
   * Member removed from project
   * Properties:
   * - project_id: string
   * - member_id: string
   * - member_role: string
   */
  project_member_removed = 'project_member_removed',

  /**
   * Pending invite cancelled
   * Properties:
   * - project_id: string
   * - invite_id: string
   */
  project_invite_cancelled = 'project_invite_cancelled',

  /**
   * User opens deployed project URL
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - deployed_url: string
   * - page: "project_dashboard"
   * - section: "home" | "version_history"
   */
  project_url_opened = 'project_url_opened',

  /**
   * User toggles task category
   * Properties:
   * - category: "product_development" | "project_setup"
   * - section: "home" | "task_list"
   * - page: "product_dashboard"
   * - page_url: string
   */
  project_task_toggled = 'project_task_toggled',

  /**
   * User clicks on a specific task
   * Properties:
   * - task_name: string
   * - category: "product_development"
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - section: "home" | "task_list"
   * - page: "product_dashboard"
   * - page_url: string
   */
  project_task_clicked = 'project_task_clicked',

  /**
   * User clicks "View All" on analytics
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - origin_section: "home"
   */
  project_analyticsViewAll_clicked = 'project_analyticsViewAll_clicked',

  /**
   * User uploads site image
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - image_type: "favicon_dark" | "favicon_light" | "social_preview"
   * - file_type: "png" | "jpg"
   * - section: "product_settings"
   * - page: "product_dashboard"
   */
  project_siteImage_uploaded = 'project_siteImage_uploaded',

  /**
   * User saves site image
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - image_type: "favicon_dark" | "favicon_light" | "social_preview"
   */
  project_siteImage_saved = 'project_siteImage_saved',

  /**
   * User uploads/selects an image locally (before hitting save button)
   * Properties:
   * - project_id: string
   * - image_type: "favicon_dark" | "favicon_light" | "og_image" | "twitter_image"
   * - file_type: string (e.g., "image/png", "image/jpeg")
   * - file_size: number (in bytes)
   */
  project_siteImage_uploaded_locally = 'project_siteImage_uploaded_locally',

  /**
   * User lands on custom domain page
   * Properties:
   * - project_id: string
   * - existing_domains_count: number
   */
  custom_domain_page_landing = 'custom_domain_page_landing',

  /**
   * User clicks "Add Domain" button
   * Properties:
   * - project_id: string
   * - domain_name: string
   * - domain_type: "apex" | "subdomain"
   */
  custom_domain_add_clicked = 'custom_domain_add_clicked',

  /**
   * User successfully adds a custom domain
   * Properties:
   * - project_id: string
   * - domain_name: string
   */
  custom_domain_added = 'custom_domain_added',

  /**
   * User clicks delete in dropdown menu
   * Properties:
   * - project_id: string
   * - domain_name: string
   * - verification_status: string
   */
  custom_domain_delete_clicked = 'custom_domain_delete_clicked',

  /**
   * User successfully deletes a custom domain
   * Properties:
   * - project_id: string
   * - domain_name: string
   */
  custom_domain_deleted = 'custom_domain_deleted',

  /**
   * User clicks refresh button to check domain status
   * Properties:
   * - project_id: string
   */
  custom_domain_refreshed = 'custom_domain_refreshed',

  /**
   * User copies DNS record value
   * Properties:
   * - project_id: string
   * - record_type: string
   * - domain_name: string
   */
  custom_domain_dns_record_copied = 'custom_domain_dns_record_copied',

  // DATABASE EVENTS (8 events)

  /**
   * User lands on database page
   * Properties:
   * - project_id: string
   * - total_tables_count: number
   */
  database_page_landing = 'database_page_landing',

  /**
   * User clicks a table in the sidebar
   * Properties:
   * - project_id: string
   * - table_name: string
   * - row_count: number
   */
  database_table_selected = 'database_table_selected',

  /**
   * User applies filters
   * Properties:
   * - project_id: string
   * - table_name: string
   * - filter_count: number
   * - filter_columns: string[]
   */
  database_filter_applied = 'database_filter_applied',

  /**
   * User sorts a column
   * Properties:
   * - project_id: string
   * - table_name: string
   * - column_name: string
   * - direction: "asc" | "desc"
   */
  database_sort_applied = 'database_sort_applied',

  /**
   * User clicks refresh button
   * Properties:
   * - project_id: string
   * - table_name: string
   * - refresh_type: "table" | "all"
   */
  database_refreshed = 'database_refreshed',

  /**
   * User shows/hides columns
   * Properties:
   * - project_id: string
   * - table_name: string
   * - visible_count: number
   * - total_count: number
   */
  database_columns_toggled = 'database_columns_toggled',

  /**
   * User navigates to next/prev page
   * Properties:
   * - project_id: string
   * - table_name: string
   * - from_page: number
   * - to_page: number
   */
  database_page_changed = 'database_page_changed',

  /**
   * User opens/closes filter panel
   * Properties:
   * - project_id: string
   * - table_name: string
   * - action: "opened" | "closed"
   */
  database_filters_toggled = 'database_filters_toggled',

  // STORAGE EVENTS (6 events)

  /**
   * User lands on storage page
   * Properties:
   * - project_id: string
   * - total_items_count: number
   */
  storage_page_landing = 'storage_page_landing',

  /**
   * User clicks on a folder to navigate into it
   * Properties:
   * - project_id: string
   * - folder_name: string
   * - folder_path: string
   */
  storage_folder_opened = 'storage_folder_opened',

  /**
   * User clicks on a file to view details
   * Properties:
   * - project_id: string
   * - file_name: string
   * - file_type: string
   * - file_size: number
   */
  storage_file_selected = 'storage_file_selected',

  /**
   * User downloads a file
   * Properties:
   * - project_id: string
   * - file_name: string
   * - file_type: string
   * - file_size: number
   */
  storage_file_downloaded = 'storage_file_downloaded',

  /**
   * User clicks breadcrumb to navigate
   * Properties:
   * - project_id: string
   * - target_path: string
   * - breadcrumb_level: number
   */
  storage_breadcrumb_clicked = 'storage_breadcrumb_clicked',

  /**
   * User clicks refresh button
   * Properties:
   * - project_id: string
   * - current_path: string
   */
  storage_refreshed = 'storage_refreshed',

  /**
   * User saves Google Analytics ID
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - provider: "google_analytics"
   * - integration_status: "connected"
   */
  project_GAId_saved = 'project_GAId_saved',

  /**
   * User saves Twitter details
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - platform: "x"
   * - fields_updated: string[]
   */
  project_twitterDetails_saved = 'project_twitterDetails_saved',

  /**
   * User saves social media details
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - platform: "x"
   * - fields_updated: string[]
   */
  project_socialMedia_saved = 'project_socialMedia_saved',

  /**
   * User selects a version from history
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - version_id: string
   * - version_name: string
   * - version_order: number
   */
  project_versionHistory_selected = 'project_versionHistory_selected',

  /**
   * User saves integrations
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - integration: "better_auth" | "resend"
   */
  project_integrations_saved = 'project_integrations_saved',

  // ============================================
  // EDITOR (5 events)
  // ============================================

  /**
   * User clicks to open project in editor
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - source: string
   */
  editor_openProject_clicked = 'editor_openProject_clicked',

  /**
   * User clicks back to dashboard from editor
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - page: "editor"
   * - source: string
   */
  editor_backToDashboard_clicked = 'editor_backToDashboard_clicked',

  /**
   * User submits message in editor chat
   * Properties:
   * - project_id: string
   * - project_name: string
   * - project_url: string
   * - page: "project_editor"
   * - message: string (the actual prompt text)
   * - has_attachment: boolean
   * - attachment_type: "png" | "pdf" | "jpg"
   * - is_selectElement_active: boolean
   * - speechToText_used: boolean
   * - selectedElement: string
   * - chatDepth: number (count of this message)
   * - source: string
   */
  editor_message_submitted = 'editor_message_submitted',

  /**
   * Error is thrown in editor
   * Properties:
   * - display_type: "chat_message" | "toast"
   * - error_name: string
   * - error_details: string
   * - page: string
   */
  editor_error_thrown = 'editor_error_thrown',

  /**
   * User switches tabs in editor
   * Properties:
   * - tab_from: "messages"
   * - tab_to: string
   */
  editor_tab_switched = 'editor_tab_switched',

  /**
   * User lands on editor page
   * Properties:
   * - project_id: string
   * - entry_point: "dashboard" | "direct_link"
   * - project_status: string
   */
  editor_page_landing = 'editor_page_landing',

  /**
   * User switches feature tabs in editor
   * Properties:
   * - project_id: string
   * - from_feature: "project-editor" | "visual-editor" | "add-feature" | "notes" | null
   * - to_feature: "project-editor" | "visual-editor" | "add-feature" | "notes"
   * - is_first_visit: boolean
   */
  editor_feature_tab_switched = 'editor_feature_tab_switched',

  // FEATURE BUILD EVENTS (15 events)

  /**
   * User navigates to "Add a new Feature" tab
   * Properties:
   * - project_id: string
   * - recent_tasks_count: number
   * - has_active_session: boolean
   */
  feature_build_panel_opened = 'feature_build_panel_opened',

  /**
   * User clicks on a recent task to reopen it
   * Properties:
   * - project_id: string
   * - session_id: string
   * - task_status: string
   * - task_age_hours: number
   */
  feature_build_recent_task_clicked = 'feature_build_recent_task_clicked',

  /**
   * User submits initial feature prompt
   * Properties:
   * - project_id: string
   * - feature_prompt: string
   * - prompt_length: number
   * - has_selected_elements: boolean
   * - element_count: number
   */
  feature_build_task_started = 'feature_build_task_started',

  /**
   * Workspace opens (either new or existing session)
   * Properties:
   * - project_id: string
   * - session_id: string
   * - has_prior_messages: boolean
   * - initial_mode: string
   */
  feature_build_session_opened = 'feature_build_session_opened',

  /**
   * Clarifying questions appear to user
   * Properties:
   * - project_id: string
   * - session_id: string
   * - question_count: number
   * - question_types: string[]
   */
  feature_build_questions_shown = 'feature_build_questions_shown',

  /**
   * User submits answers to questions
   * Properties:
   * - project_id: string
   * - session_id: string
   * - question_count: number
   * - answer_types: string[]
   * - answers: Array<{ question_id: string, answer_text: string, answer_type: string }>
   */
  feature_build_question_answered = 'feature_build_question_answered',

  /**
   * User clicks back button to close workspace
   * Properties:
   * - project_id: string
   * - session_id: string
   */
  feature_build_workspace_closed = 'feature_build_workspace_closed',

  /**
   * Plan generation completes and is ready for review
   * Properties:
   * - project_id: string
   * - session_id: string
   * - plan_status: string
   * - had_questions: boolean
   */
  feature_build_plan_ready = 'feature_build_plan_ready',

  /**
   * User clicks "Approve" button on plan
   * Properties:
   * - project_id: string
   * - session_id: string
   */
  feature_build_plan_approved = 'feature_build_plan_approved',

  /**
   * User sends message to refine the plan
   * Properties:
   * - project_id: string
   * - session_id: string
   * - refinement_message_length: number
   * - current_mode: string
   */
  feature_build_plan_refinement_sent = 'feature_build_plan_refinement_sent',

  /**
   * User cancels the feature build session
   * Properties:
   * - project_id: string
   * - session_id: string
   * - mode_at_cancellation: string
   * - stage_at_cancellation: string
   */
  feature_build_plan_cancelled = 'feature_build_plan_cancelled',

  /**
   * User tries to interact but another feature is executing
   * Properties:
   * - project_id: string
   * - session_id: string
   * - locked_by_session_id: string
   * - attempted_action: string
   */
  feature_build_lock_encountered = 'feature_build_lock_encountered',

  /**
   * Execution begins after plan approval
   * Properties:
   * - project_id: string
   * - session_id: string
   * - execution_id: string
   */
  feature_build_execution_started = 'feature_build_execution_started',

  /**
   * Execution finishes (success or failure)
   * Properties:
   * - project_id: string
   * - session_id: string
   * - execution_id: string
   * - status: "completed" | "failed"
   * - error_message: string | undefined
   */
  feature_build_execution_completed = 'feature_build_execution_completed',

  /**
   * User sends message in interactive mode after execution
   * Properties:
   * - project_id: string
   * - session_id: string
   * - message_length: number
   */
  feature_build_followup_message_sent = 'feature_build_followup_message_sent',

  /**
   * User toggles chat panel (Ctrl+B or button)
   * Properties:
   * - project_id: string
   * - action: "opened" | "closed"
   * - trigger: "button" | "keyboard"
   */
  editor_chat_panel_toggled = 'editor_chat_panel_toggled',

  /**
   * User clicks refresh in editor preview
   * Properties:
   * - project_id: string
   * - page: "editor"
   * - current_route: string
   */
  editor_refresh_clicked = 'editor_refresh_clicked',

  /**
   * User clicks external link in editor preview
   * Properties:
   * - project_id: string
   * - page: "editor"
   * - current_route: string
   */
  editor_external_link_clicked = 'editor_external_link_clicked',

  /**
   * User switches device in editor preview
   * Properties:
   * - project_id: string
   * - from_device: "desktop" | "mobile" | "tablet"
   * - to_device: "desktop" | "mobile" | "tablet"
   */
  editor_device_switched = 'editor_device_switched',

  /**
   * User navigates to different route in editor preview
   * Properties:
   * - project_id: string
   * - from_route: string | null
   * - to_route: string
   * - trigger: "dropdown" | "back_button" | "forward_button" | "url_bar"
   */
  editor_route_changed = 'editor_route_changed',

  /**
   * User clicks browser navigation buttons in editor
   * Properties:
   * - project_id: string
   * - direction: "back" | "forward"
   * - current_route: string
   */
  editor_navigation_button_clicked = 'editor_navigation_button_clicked',

  /**
   * User toggles notes panel in editor
   * Properties:
   * - project_id: string
   * - action: "opened" | "closed"
   */
  editor_notes_panel_toggled = 'editor_notes_panel_toggled',

  /**
   * User initiates deployment from editor
   * Properties:
   * - project_id: string
   * - trigger: "toolbar_button"
   */
  editor_deploy_initiated = 'editor_deploy_initiated',

  /**
   * User stops active generation in editor chat
   * Properties:
   * - project_id: string
   * - progress_percentage: number | null
   */
  editor_generation_stopped = 'editor_generation_stopped',

  /**
   * User toggles element selection mode in editor
   * Properties:
   * - project_id: string
   * - enabled: boolean
   */
  editor_select_mode_toggled = 'editor_select_mode_toggled',

  /**
   * Insufficient balance error shown in editor
   * Properties:
   * - project_id: string
   * - current_balance: number
   * - action_attempted: "send_message"
   */
  editor_insufficient_balance_shown = 'editor_insufficient_balance_shown',

  // ============================================
  // THEME EDITOR (7 events)
  // ============================================

  /**
   * User clicks to open theme editor
   * Properties: source: "project_editor"
   */
  theme_editor_clicked = 'theme_editor_clicked',

  /**
   * User lands on theme editor
   * Properties: source: "project_editor"
   */
  theme_editor_landing = 'theme_editor_landing',

  /**
   * User switches theme tabs
   * Properties: tab_name: "colors" | "typography" | "spacing" | "shadow"
   */
  theme_tab_switched = 'theme_tab_switched',

  /**
   * User changes theme property
   * Properties:
   * - project_id: string
   * - theme_category: "colors"
   * - property_name: "primary_color"
   * - change_type: "manual"
   * - from_value: string
   * - to_value: string
   */
  theme_property_changed = 'theme_property_changed',

  /**
   * User changes theme mode
   * Properties:
   * - from: "dark"
   * - to: "light"
   */
  theme_mode_changed = 'theme_mode_changed',

  /**
   * User clicks theme reset
   * Properties:
   * - from: { theme_category, property_name }
   * - to: { theme_category, property_name }
   */
  theme_reset_clicked = 'theme_reset_clicked',

  /**
   * User saves theme edits
   * Properties:
   * - from: { theme_category, property_name }
   * - to: { theme_category, property_name }
   */
  theme_edits_saved = 'theme_edits_saved',

  /**
   * User opens theme editor panel
   * Properties:
   * - project_id: string
   */
  theme_editor_opened = 'theme_editor_opened',

  /**
   * User saves theme changes
   * Properties:
   * - project_id: string
   * - properties_changed: string[]
   */
  theme_editor_saved = 'theme_editor_saved',

  /**
   * User resets theme to defaults
   * Properties:
   * - project_id: string
   */
  theme_editor_reset = 'theme_editor_reset',

  // ============================================
  // DEPLOYMENT (1 event)
  // ============================================

  /** Project deployed successfully */
  project_deployed_successfully = 'project_deployed_successfully',

  // ============================================
  // VERSION HISTORY (6 events)
  // ============================================

  /**
   * User toggles show hidden versions
   * Properties:
   * - project_id: string
   * - show_hidden: boolean
   */
  version_history_hidden_toggled = 'version_history_hidden_toggled',

  /**
   * User previews a version
   * Properties:
   * - project_id: string
   * - version_id: string
   * - version_number: number
   */
  version_history_preview_clicked = 'version_history_preview_clicked',

  /**
   * User restores a version
   * Properties:
   * - project_id: string
   * - version_id: string
   * - version_number: number
   * - from_version: string
   */
  version_history_restore_clicked = 'version_history_restore_clicked',

  /**
   * Version restore succeeds
   * Properties:
   * - project_id: string
   * - version_id: string
   */
  version_history_restore_success = 'version_history_restore_success',

  /**
   * Version restore fails
   * Properties:
   * - project_id: string
   * - version_id: string
   * - error_message: string
   */
  version_history_restore_error = 'version_history_restore_error',

  /**
   * User sets version as production
   * Properties:
   * - project_id: string
   * - version_id: string
   * - version_number: number
   */
  version_history_set_production = 'version_history_set_production',

  // ============================================
  // NOTES / COMMENTS (8 events)
  // ============================================

  /**
   * User switches editor mode
   * Properties:
   * - from: "editor"
   * - to: "notes"
   */
  editor_mode_switched = 'editor_mode_switched',

  /** User lands on notes section */
  editor_notes_landing = 'editor_notes_landing',

  /**
   * User stops/exits notes mode
   * Properties:
   * - project_id: string
   */
  editor_notes_stopped = 'editor_notes_stopped',

  /**
   * User initiates a comment
   * Properties: comment_id: string
   */
  notes_comment_initiated = 'notes_comment_initiated',

  /**
   * User submits a comment (before API call)
   * Properties:
   * - project_id: string
   * - text: string
   * - has_attachment: boolean
   */
  notes_comment_submitted = 'notes_comment_submitted',

  /**
   * Comment successfully created in DB
   * Properties:
   * - project_id: string
   * - comment_id: string
   * - has_attachment: boolean
   * - text: string (the commented text)
   */
  notes_comment_created = 'notes_comment_created',

  /**
   * User edits a comment
   * Properties:
   * - project_id: string
   * - comment_id: string
   * - has_attachment: boolean
   * - text_changed: boolean
   * - files_changed: boolean
   */
  notes_comment_edited = 'notes_comment_edited',

  /**
   * User closes a comment
   * Properties:
   * - closure_type: "resolve" | "run" | "remove"
   * - comment_id: string
   * - text: string
   */
  notes_comment_closed = 'notes_comment_closed',

  /**
   * User toggles notes navigation
   * Properties:
   * - from: "on"
   * - to: "off"
   */
  notes_navigate_toggled = 'notes_navigate_toggled',

  /**
   * User toggles notes filter
   * Properties:
   * - from: "open"
   * - to: "closed"
   */
  notes_filter_toggled = 'notes_filter_toggled',

  // ============================================
  // VISUAL EDITOR (3 events)
  // ============================================

  /** User lands on visual editor */
  visual_editor_landing = 'visual_editor_landing',

  /**
   * User stops/exits visual editor mode
   * Properties:
   * - project_id: string
   * - has_unsaved_changes: boolean
   */
  visual_editor_stopped = 'visual_editor_stopped',

  /**
   * User selects element in visual editor
   * Properties:
   * - project_id: string
   * - element_type: "text" | "button" | "container" | "image"
   */
  visual_element_selected = 'visual_element_selected',

  /**
   * User changes visual property
   * Properties: batched from and to values that were changed
   */
  visual_property_changed = 'visual_property_changed',

  /**
   * User saves visual editor changes
   * Properties:
   * - project_id: string
   * - changes_count: number
   * - modification_types: string[] (e.g., ["className", "imageSrc"])
   * - from_classes: string[] (original classes before changes)
   * - to_classes: string[] (new classes after changes)
   */
  visual_editor_changes_saved = 'visual_editor_changes_saved',

  // ============================================
  // QUESTIONNAIRE (5 events)
  // ============================================

  /**
   * User lands on questionnaire page
   * Properties:
   * - project_id: string
   * - name: "preview_question" | "none if resumed"
   * - question_number: number
   * - questions_remaining: number
   * - question_title: string
   * - all_options: string[]
   * - unselected_options: object
   * - additional_thoughts: string
   */
  questionnaire_page_landing = 'questionnaire_page_landing',

  /**
   * User answers a question
   * Properties:
   * - type: "role" | "single select" | "multiselect"
   * - question_id: string
   * - project_id: string
   * - question_number: number
   * - questions_answered: number
   * - question_title: string
   * - all_options: string[]
   * - selected_options: string[] (empty for hierarchical-features to avoid bloated payloads)
   * - additional_thoughts: string (MCQ: additional thoughts textarea, Q1: customRequirementsText, Q4/integrations: empty)
   * - custom_roles: string[] | null (custom role names added, only for role-confirmation)
   * - custom_roles_count: number | null
   * - edited_roles: string[] | null (original role names that were edited, only for role-confirmation)
   * - edited_roles_count: number | null
   * - removed_roles: string[] | null (role names removed, only for role-confirmation)
   * - removed_roles_count: number | null
   * - ambiguous_accepted_count: number | null (ambiguous roles accepted, only for role-confirmation)
   * - ambiguous_rejected_count: number | null (ambiguous roles rejected/skipped, only for role-confirmation)
   * - ambiguous_total_count: number | null (total ambiguous roles, only for role-confirmation)
   * - custom_stories: string[] | null (custom story texts, only for hierarchical-features)
   * - custom_stories_count: number | null
   * - edited_stories: null (reserved, only for hierarchical-features)
   * - edited_stories_count: number | null
   * - total_roles: number | null (total role tabs, only for hierarchical-features)
   * - roles_with_selections: number | null (roles with ≥1 story selected, only for hierarchical-features)
   * - stories_per_role: object | null (breakdown of selections by role, only for hierarchical-features)
   * - avg_stories_per_role: number | null (mean selections per role, only for hierarchical-features)
   * - role_with_most_stories: string | null (role with most selections, only for hierarchical-features)
   * - role_with_least_stories: string | null (role with fewest selections, only for hierarchical-features)
   * - total_features: number | null (unique features across all roles, only for hierarchical-features)
   * - ai_stories_selected: number | null (AI-generated stories selected, only for hierarchical-features)
   * - total_stories_selected: number | null (total stories selected across all roles, only for hierarchical-features)
   * - custom_integrations: string[] | null (custom integration names, only for external-integrations)
   * - custom_integrations_count: number | null
   * - detected_integrations_count: number | null (detected/alternatives chosen, only for external-integrations)
   * - categories_selected: string[] | null (integration categories selected, only for external-integrations)
   * - selected_count: number (how many options selected)
   * - rejected_options: string[] - Options explicitly rejected (X button)
   * - rejected_count: number - Number of rejected options
   * - has_additional_thoughts: boolean (whether user added custom text)
   * - additional_thoughts_length: number (length of custom text input)
   */
  question_answered = 'question_answered',

  /**
   * User adds a story to question
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role: "admin"
   * - category: "login"
   * - story_added: string
   * - question_number: number
   * - questions_answered: number
   */
  question_story_added = 'question_story_added',

  /**
   * User edits a story
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role: "admin"
   * - category: "login"
   * - from: string
   * - to: string
   * - question_number: number
   * - questions_answered: number
   */
  question_story_edited = 'question_story_edited',

  /**
   * User selects/deselects a story
   * Properties:
   * - project_id: string
   * - question_id: string
   * - story_id: string
   * - story_text: string
   * - role: string
   * - feature: string
   * - action: "selected" | "deselected"
   * - is_custom: boolean
   * - question_number: number
   * - questions_answered: number
   */
  question_story_selected = 'question_story_selected',

  /**
   * User clicks a role tab in feature stories question
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role_from: string
   * - role_to: string
   * - role_index: number
   * - is_first_visit: boolean
   * - question_number: number
   * - questions_answered: number
   */
  question_feature_role_tab_clicked = 'question_feature_role_tab_clicked',

  /**
   * Warning modal appears because user hasn't visited all role tabs
   * Properties:
   * - project_id: string
   * - question_id: string
   * - total_roles: number
   * - roles_visited: number
   * - roles_unvisited: number
   * - unvisited_role_names: string[]
   * - question_number: number
   * - questions_answered: number
   */
  question_feature_unvisited_tabs_warning_shown = 'question_feature_unvisited_tabs_warning_shown',

  /**
   * User acknowledges unvisited tabs warning and proceeds anyway
   * Properties:
   * - project_id: string
   * - question_id: string
   * - roles_still_unvisited: number
   * - action: "acknowledged"
   * - question_number: number
   * - questions_answered: number
   */
  question_feature_unvisited_tabs_warning_acknowledged = 'question_feature_unvisited_tabs_warning_acknowledged',

  /**
   * User regenerates an option in MCQ question
   * Properties:
   * - project_id: string
   * - question_id: string
   * - option_id: string
   * - option_text: string
   * - question_type: string
   * - question_number: number
   * - questions_answered: number
   */
  question_option_regenerated = 'question_option_regenerated',

  /**
   * User selects (accepts/rejects) an option in MCQ question
   * Properties:
   * - project_id: string
   * - question_id: string
   * - option_id: string
   * - option_text: string
   * - action: "accepted" | "rejected"
   * - question_type: string
   * - question_number: number
   * - questions_answered: number
   */
  question_option_selected = 'question_option_selected',

  /**
   * User adds a custom role in role confirmation
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role_name: string
   * - role_description: string
   * - question_number: number
   * - questions_answered: number
   */
  question_role_added = 'question_role_added',

  /**
   * User edits a role in role confirmation
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role_id: string
   * - from_name: string
   * - to_name: string
   * - from_description: string
   * - to_description: string
   * - question_number: number
   * - questions_answered: number
   */
  question_role_edited = 'question_role_edited',

  /**
   * User removes a role in role confirmation
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role_id: string
   * - role_name: string
   * - role_type: "confirmed" | "custom" | "ambiguous"
   * - question_number: number
   * - questions_answered: number
   */
  question_role_removed = 'question_role_removed',

  /**
   * User restores a previously removed role
   * Properties:
   * - project_id: string
   * - question_id: string
   * - role_id: string
   * - role_name: string
   * - restore_type: "confirmed" | "ambiguous"
   * - question_number: number
   * - questions_answered: number
   */
  question_role_restored = 'question_role_restored',

  /**
   * User selects/deselects a regular integration
   * Properties:
   * - project_id: string
   * - question_id: string
   * - integration_id: string
   * - integration_name: string
   * - category: string
   * - action: "selected" | "deselected"
   * - is_detected: boolean
   * - question_number: number
   * - questions_answered: number
   */
  question_integration_selected = 'question_integration_selected',

  /**
   * User completes an integration category
   * Properties:
   * - project_id: string
   * - question_id: string
   * - category: string
   * - integration_selected: string
   * - category_index: number
   * - total_categories: number
   * - question_number: number
   * - questions_answered: number
   */
  question_integration_category_completed = 'question_integration_category_completed',

  /**
   * User selects/deselects a custom integration (not in catalog)
   * Properties:
   * - project_id: string
   * - question_id: string
   * - integration_id: string
   * - integration_name: string
   * - mentioned_as: string
   * - category: string
   * - action: "selected" | "deselected"
   * - has_alternatives: boolean
   * - question_number: number
   * - questions_answered: number
   */
  question_integration_custom_selected = 'question_integration_custom_selected',

  /**
   * User selects/deselects an alternative for a custom integration
   * Properties:
   * - project_id: string
   * - question_id: string
   * - custom_integration_id: string
   * - custom_integration_name: string
   * - alternative_id: string
   * - alternative_name: string
   * - action: "selected" | "deselected"
   * - category: string
   * - question_number: number
   * - questions_answered: number
   */
  question_integration_alternative_selected = 'question_integration_alternative_selected',

  /**
   * User continues after reviewing explicit unknown integrations
   * Properties:
   * - project_id: string
   * - question_id: string
   * - total_unknown: number
   * - custom_selected_count: number
   * - alternatives_selected_count: number
   * - skipped_count: number
   * - question_number: number
   * - questions_answered: number
   */
  question_integration_unknown_continued = 'question_integration_unknown_continued',

  /**
   * User clicks to view question history
   * Properties:
   * - project_id: string
   * - question_number: number
   * - questions_answered: number
   * - current_question_id: string
   */
  question_history_opened = 'question_history_opened',

  /**
   * User completes questionnaire
   * Properties:
   * - time_taken: number | null (in seconds, null if start time not captured)
   * - total_responses: number
   */
  questionnaire_completed = 'questionnaire_completed',

  // ============================================
  // FRS (8 events)
  // ============================================

  /** User downloads FRS summary */
  frs_summary_downloaded = 'frs_summary_downloaded',

  /**
   * User edits FRS banner
   * Properties:
   * - button_name: "change_cover" | "reposition"
   * - action_name: "browse" | "completed"
   * - type: "image_upload" | "link_submit" | "save_position"
   */
  frs_banner_edited = 'frs_banner_edited',

  /**
   * User lands on FRS page
   * Properties:
   * - project_id: string
   * - frs_status: "ready" | "generating" | "pending" | "error"
   * - has_theme: boolean
   */
  frs_page_landing = 'frs_page_landing',

  /**
   * User switches between FRS tabs
   * Properties:
   * - project_id: string
   * - tab_from: "summary" | "questionnaire-responses"
   * - tab_to: "summary" | "questionnaire-responses"
   */
  frs_tab_switched = 'frs_tab_switched',

  /**
   * User expands an FRS section accordion
   * Properties:
   * - project_id: string
   * - section_type: "product_overview" | "features" | "user_roles" | "user_stories" | "integrations" | "conclusion"
   * - section_name: string
   */
  frs_section_expanded = 'frs_section_expanded',

  /**
   * User collapses an FRS section accordion
   * Properties:
   * - project_id: string
   * - section_type: "product_overview" | "features" | "user_roles" | "user_stories" | "integrations" | "conclusion"
   * - section_name: string
   */
  frs_section_collapsed = 'frs_section_collapsed',

  /**
   * FRS generation completes successfully
   * Properties:
   * - project_id: string
   */
  frs_generation_completed = 'frs_generation_completed',

  /**
   * FRS generation fails
   * Properties:
   * - project_id: string
   * - error_message: string
   */
  frs_generation_error = 'frs_generation_error',

  // ============================================
  // DESIGN CUSTOMIZATION (13 events)
  // ============================================

  /**
   * User lands on design theme selection page
   * Properties:
   * - project_id: string
   */
  design_theme_landing = 'design_theme_landing',

  /**
   * User selects a design theme (LEGACY - consider using theme_preset_selected)
   * Properties:
   * - theme_selected: "airbnb" | "custom"
   * - theme_meta: object (theme values: color, font, etc.)
   */
  design_theme_selected = 'design_theme_selected',

  /**
   * User toggles AI design mode on/off
   * Properties:
   * - project_id: string
   * - enabled: boolean - true if turning ON AI design, false if turning OFF
   * - from_state: "manual" | "ai_design" - Previous state
   * - to_state: "manual" | "ai_design" - New state
   * - had_customizations: boolean - Whether user had made manual changes before toggling
   */
  design_theme_ai_design_toggled = 'design_theme_ai_design_toggled',

  /**
   * User selects theme mode (Light/Dark/System)
   * Properties:
   * - project_id: string
   * - from_mode: "light" | "dark" | "system" | null - Previous mode (null on first selection)
   * - to_mode: "light" | "dark" | "system" - Selected mode
   * - is_first_selection: boolean - Whether this is the first mode choice
   */
  design_theme_mode_selected = 'design_theme_mode_selected',

  /**
   * User clicks a template preset button
   * Properties:
   * - project_id: string
   * - preset_name: string - "airbnb" | "apple" | "google" | "openai" | "slack" | "spotify" | "stripe" | "coinbase" | "vercel" | "vscode"
   * - from_preset: string | null - Previous preset (null if first, "Custom" if had manual changes)
   * - preset_index: number - Position in the list
   * - theme_mode: "light" | "dark" | "system" - Current theme mode when selected
   */
  design_theme_preset_selected = 'design_theme_preset_selected',

  /**
   * User clicks "Randomize Theme" button
   * Properties:
   * - project_id: string
   * - from_preset: string - Previous preset before randomization
   * - to_preset: string - Randomly selected preset
   * - randomize_count: number - How many times user clicked randomize (session-scoped)
   */
  design_theme_randomize_clicked = 'design_theme_randomize_clicked',

  /**
   * User clicks a tab in the theme customizer
   * Properties:
   * - project_id: string
   * - from_tab: string | null - Previous tab ("template-themes" | "color-palette" | "font-typography" | "spacing-radius" | "shadows-depth")
   * - to_tab: string - Clicked tab
   * - tab_index: number - Position (0-4)
   * - is_first_visit: boolean - Whether this tab was clicked for first time
   * - ai_design_enabled: boolean - Current AI design state
   */
  design_theme_tab_clicked = 'design_theme_tab_clicked',

  /**
   * User clicks "Continue to Frontend" button (before confirmation dialog)
   * Properties:
   * - project_id: string
   * - ai_design_used: boolean - Whether AI design mode was used
   * - final_preset: string | null - Final preset ("Custom" if customized, preset name if used as-is, null if AI)
   * - theme_mode: "light" | "dark" | "system" - Final mode selection
   */
  design_theme_frontend_generation_initiated = 'design_theme_frontend_generation_initiated',

  /**
   * User clicks "Generate Frontend" in confirmation dialog
   * Properties:
   * - project_id: string
   * - action: "confirmed"
   * - ai_design_used: boolean
   * - final_preset: string | null
   */
  design_theme_frontend_generation_confirmed = 'design_theme_frontend_generation_confirmed',

  /**
   * User clicks "Cancel" in confirmation dialog
   * Properties:
   * - project_id: string
   * - action: "cancelled"
   * - ai_design_used: boolean
   */
  design_theme_frontend_generation_cancelled = 'design_theme_frontend_generation_cancelled',

  /**
   * Theme API submission succeeds
   * Properties:
   * - project_id: string
   * - ai_design_used: boolean
   * - final_preset: string | null
   */
  design_theme_submission_success = 'design_theme_submission_success',

  /**
   * Theme API submission fails
   * Properties:
   * - project_id: string
   * - error_message: string
   * - ai_design_used: boolean
   * - final_preset: string | null
   */
  design_theme_submission_error = 'design_theme_submission_error',

  /**
   * User clicks "Back to FRS" button
   * Properties:
   * - project_id: string
   * - ai_design_used: boolean
   * - had_customizations: boolean - Whether user made any changes
   */
  design_theme_back_to_frs_clicked = 'design_theme_back_to_frs_clicked',

  /**
   * User clicks continue button (Generic - consider using specific events)
   * Properties:
   * - button_name: "To Theme Selection" | "To Wireframe" | "Approve Wireframes" (legacy, optional)
   * - page: "frs" | "theme" | "wireframes" (which page the button was clicked from)
   * - project_id: string
   * - destination: "theme" | "wireframes" (where the button leads to)
   * - has_theme: boolean (context for FRS page)
   */
  continue_button_clicked = 'continue_button_clicked',

  // ============================================
  // WIREFRAMES (23 events)
  // ============================================

  /**
   * User lands on wireframe page
   * Properties:
   * - project_id: string
   * - status: "pending" | "generating" | "ready" | "error"
   */
  wireframe_page_landing = 'wireframe_page_landing',

  /**
   * User clicks wireframe dropdown
   * Properties:
   * - type: "browse" | "selected"
   * - selectedValue: string (name of the persona)
   */
  wireframe_dropdown_clicked = 'wireframe_dropdown_clicked',

  /**
   * User submits prompt edit in wireframe
   * Properties:
   * - project_id: string
   * - message: string - The actual message content
   * - message_length: number - Character count
   * - has_attachment: boolean
   * - select_mode_active: boolean - Was element selected?
   * - selected_element_types: string[] - Array of selected element tag names (e.g., ["button", "div"])
   * - total_messages: number - Total messages (user + assistant)
   * - user_messages: number - User messages only
   * - assistant_messages: number - Assistant messages only
   */
  wireframe_prompt_edit_submitted = 'wireframe_prompt_edit_submitted',

  /**
   * User clicks to attach file in prompt
   * Properties:
   * - type: "browse" | "selected"
   * - file_type: "pdf" | "doc"
   */
  prompt_attach_clicked = 'prompt_attach_clicked',

  /**
   * User clicks share icon
   * Properties:
   * - action: "browse" | "invited"
   * - access: "owner" | "admin" | "viewer"
   * - member_count: number
   * - email: string
   */
  share_icon_clicked = 'share_icon_clicked',

  /**
   * User edits project name
   * Properties:
   * - action: "browse" | "saved"
   * - old_name: string
   * - new_name: string
   */
  project_name_edited = 'project_name_edited',

  /** User clicks support icon */
  support_icon_clicked = 'support_icon_clicked',

  // ============================================
  // WIREFRAMES - Generation & Loading Events
  // ============================================

  /**
   * Wireframe generation begins (user enters waitroom)
   * Properties:
   * - project_id: string
   * - trigger: "initial" | "retry" - First generation or retry after error
   */
  wireframe_generation_started = 'wireframe_generation_started',

  /**
   * Progress updates during generation (sampled every 10% or key milestones)
   * Properties:
   * - project_id: string
   * - progress_percentage: number - 0-100
   * - step_description: string | null - Current step (e.g., "Shaping your frontend")
   */
  wireframe_generation_progress = 'wireframe_generation_progress',

  /**
   * Generation successfully completes
   * Properties:
   * - project_id: string
   * - total_duration_seconds: number
   * - screen_count: number - Number of screens generated
   */
  wireframe_generation_completed = 'wireframe_generation_completed',

  /**
   * Generation fails
   * Properties:
   * - project_id: string
   * - error_message: string
   */
  wireframe_generation_error = 'wireframe_generation_error',

  /**
   * User clicks "Retry Generation" after error
   * Properties:
   * - project_id: string
   * - previous_error: string
   */
  wireframe_generation_retry_clicked = 'wireframe_generation_retry_clicked',

  /**
   * User clicks "Go to Frontend Editor" after generation completes
   * Properties:
   * - project_id: string
   * - generation_duration_seconds: number
   */
  wireframe_waitroom_continue_clicked = 'wireframe_waitroom_continue_clicked',

  /**
   * User clicks "Preview Your Build" after build completes
   * Properties:
   * - project_id: string
   * - build_duration_seconds: number
   */
  build_waitroom_preview_clicked = 'build_waitroom_preview_clicked',

  // ============================================
  // WIREFRAMES - Chat Panel Events
  // ============================================

  /**
   * User toggles chat panel visibility (button or Ctrl+B)
   * Properties:
   * - project_id: string
   * - action: "open" | "close"
   * - trigger: "button" | "keyboard" - How it was toggled
   * - previous_state: "open" | "closed"
   */
  wireframe_chat_panel_toggled = 'wireframe_chat_panel_toggled',

  /**
   * User toggles element selection mode
   * Properties:
   * - project_id: string
   * - enabled: boolean - true if turning ON, false if turning OFF
   */
  wireframe_select_mode_toggled = 'wireframe_select_mode_toggled',

  /**
   * User clicks cancel during active generation
   * Properties:
   * - project_id: string
   * - progress_at_cancel: number - Progress percentage when cancelled
   */
  wireframe_generation_cancelled = 'wireframe_generation_cancelled',

  /**
   * Insufficient balance modal appears (412 error)
   * Properties:
   * - project_id: string
   * - current_balance: number
   * - action_attempted: "send_message" | "approve_frontend"
   */
  wireframe_insufficient_balance_shown = 'wireframe_insufficient_balance_shown',

  // ============================================
  // WIREFRAMES - Preview Panel Events
  // ============================================

  /**
   * User changes preview device type
   * Properties:
   * - project_id: string
   * - from_device: "desktop" | "mobile" | "tablet"
   * - to_device: "desktop" | "mobile" | "tablet"
   */
  wireframe_device_switched = 'wireframe_device_switched',

  /**
   * User navigates to different route/page (enhanced version of wireframe_dropdown_clicked)
   * Properties:
   * - project_id: string
   * - from_route: string | null - Previous route path
   * - to_route: string - New route path
   * - trigger: "dropdown" | "back_button" | "forward_button" | "url_bar" - How navigation happened
   */
  wireframe_route_changed = 'wireframe_route_changed',

  /**
   * User clicks back or forward browser buttons
   * Properties:
   * - project_id: string
   * - direction: "back" | "forward"
   * - current_route: string
   */
  wireframe_navigation_button_clicked = 'wireframe_navigation_button_clicked',

  /**
   * User clicks refresh button to reload iframe
   * Properties:
   * - project_id: string
   * - current_route: string
   * - current_device: "desktop" | "mobile" | "tablet"
   */
  wireframe_refresh_clicked = 'wireframe_refresh_clicked',

  // ============================================
  // WIREFRAMES - Approval Flow Events (CRITICAL)
  // ============================================

  /**
   * User clicks "Approve Frontend" button
   * Properties:
   * - project_id: string
   * - total_messages: number - Total messages (user + assistant)
   * - user_messages: number - User messages only
   * - assistant_messages: number - Assistant messages only
   * - current_device: "desktop" | "mobile" | "tablet" - Device view when approved
   */
  wireframe_approve_button_clicked = 'wireframe_approve_button_clicked',

  /**
   * User minimizes or restores the approval popover
   * Properties:
   * - project_id: string
   * - action: "minimize" | "restore"
   */
  wireframe_approve_popover_minimized = 'wireframe_approve_popover_minimized',

  /**
   * Confirmation dialog appears after clicking approve
   * Properties:
   * - project_id: string
   */
  wireframe_approval_dialog_shown = 'wireframe_approval_dialog_shown',

  /**
   * User clicks "Approve & Continue" in dialog
   * Properties:
   * - project_id: string
   * - total_messages: number - Total messages (user + assistant)
   * - user_messages: number - User messages only
   * - assistant_messages: number - Assistant messages only
   */
  wireframe_approval_confirmed = 'wireframe_approval_confirmed',

  /**
   * User clicks "Cancel" in approval dialog
   * Properties:
   * - project_id: string
   */
  wireframe_approval_cancelled = 'wireframe_approval_cancelled',

  /**
   * updatePricingState API call succeeds
   * Properties:
   * - project_id: string
   * - total_messages: number - Total messages (user + assistant)
   * - user_messages: number - User messages only
   * - assistant_messages: number - Assistant messages only
   */
  wireframe_submission_success = 'wireframe_submission_success',

  /**
   * updatePricingState API call fails
   * Properties:
   * - project_id: string
   * - error_message: string
   */
  wireframe_submission_error = 'wireframe_submission_error',

  /**
   * User clicks invite button to share wireframe
   * Properties:
   * - project_id: string
   * - action: "opened" - When popover opens
   */
  wireframe_invite_button_clicked = 'wireframe_invite_button_clicked',

  /**
   * User clicks project dropdown menu
   * Properties:
   * - project_id: string
   * - action: "opened" | "closed"
   * - menu_item_clicked: "back_to_dashboard" | "rename_project" | "view_wallet" | null
   */
  wireframe_project_dropdown_clicked = 'wireframe_project_dropdown_clicked',

  /**
   * User clicks to open wireframe in new tab
   * Properties:
   * - project_id: string
   * - current_route: string
   * - current_device: "desktop" | "mobile" | "tablet"
   */
  wireframe_external_link_clicked = 'wireframe_external_link_clicked',

  /**
   * User starts renaming a project
   * Properties:
   * - project_id: string
   * - current_name: string
   */
  wireframe_project_rename_started = 'wireframe_project_rename_started',

  /**
   * User successfully completes project rename
   * Properties:
   * - project_id: string
   * - old_name: string
   * - new_name: string
   * - name_length: number
   */
  wireframe_project_rename_completed = 'wireframe_project_rename_completed',

  /**
   * User cancels project rename
   * Properties:
   * - project_id: string
   * - attempted_name: string
   */
  wireframe_project_rename_cancelled = 'wireframe_project_rename_cancelled',

  // ============================================
  // PRICING (11 events)
  // ============================================

  /**
   * User lands on pricing page after wireframe approval
   * Properties:
   * - project_id: string
   * - animation_autoplay: boolean
   * - entry_point: "wireframe_approval"
   */
  pricing_page_landing = 'pricing_page_landing',

  /**
   * User toggles between monthly/yearly billing
   * Properties:
   * - project_id: string
   * - from_cycle: "monthly" | "yearly" | null
   * - to_cycle: "monthly" | "yearly"
   * - monthly_price: number
   * - yearly_price: number
   * - yearly_savings: number
   */
  pricing_billing_cycle_toggled = 'pricing_billing_cycle_toggled',

  /**
   * User hovers over feature tooltip
   * Properties:
   * - project_id: string
   * - feature_category: string
   */
  pricing_feature_tooltip_shown = 'pricing_feature_tooltip_shown',

  /**
   * User clicks "Pay now" button
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   * - total_price: number
   */
  pricing_pay_now_clicked = 'pricing_pay_now_clicked',

  /**
   * Confirmation dialog opens showing payment details
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   * - monthly_charge: number
   * - yearly_total: number (only if yearly)
   */
  pricing_confirmation_dialog_opened = 'pricing_confirmation_dialog_opened',

  /**
   * User clicks "Continue to Payment" in confirmation dialog
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   * - total_price: number
   */
  pricing_payment_confirmed = 'pricing_payment_confirmed',

  /**
   * User clicks "Cancel" in confirmation dialog
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   */
  pricing_payment_cancelled = 'pricing_payment_cancelled',

  /**
   * User clicks "Contact Sales" button
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   */
  pricing_contact_sales_clicked = 'pricing_contact_sales_clicked',

  /**
   * Payment session created with Stripe
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   * - session_id: string (optional)
   */
  pricing_payment_session_created = 'pricing_payment_session_created',

  /**
   * Wireframes approved via pricing flow
   * Properties:
   * - project_id: string
   * - approval_method: "payment"
   */
  pricing_wireframes_approved = 'pricing_wireframes_approved',

  /**
   * Payment session creation fails
   * Properties:
   * - project_id: string
   * - billing_cycle: "monthly" | "yearly"
   * - error_message: string
   */
  pricing_payment_session_error = 'pricing_payment_session_error',
}
